// src/pages/DashboardPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Grid,
  Dialog,
  CircularProgress,
  Alert,
  Box,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { PageIndices } from '../common/i-doc/i-doc.registry.ts';
import { DashboardLayout } from '../components/DashboardLayout';
import { ChangePasswordForm } from '../components/ChangePasswordForm';
import { useAuth } from '../hooks/useAuth';
import { WaterIntakeWidget } from '../components/dashboard/WaterIntakeWidget';
import { NutritionWidget } from '../components/dashboard/NutritionWidget';
import { FoodEventDetailModal } from '../components/dashboard/FoodEventDetailModal';
import { QuickFoodAddModal } from '../components/dashboard/QuickFoodAddModal';
import { QuickWorkoutAddModal } from '../components/dashboard/QuickWorkoutAddModal';
import { QuickMedicationAddModal } from '../components/dashboard/QuickMedicationAddModal';
import { InitialSurveyModal } from '../components/dashboard/InitialSurveyModal';
import { EmailVerificationModal } from '../components/dashboard/EmailVerificationModal';
import { getTodayDashboard, getDashboardOptions } from '../services/dashboard.service';
import { TodayDashboardResponse, DashboardOptionsResponse, CaloriesStats, MacroDetail, WaterIntakeStats, WorkoutSummary } from '../types/dashboard.types';
import { FeelingsWidget } from '../components/dashboard/FeelingsWidget';
import { MedicationsWidget } from '../components/dashboard/MedicationsWidget';
import { WorkoutsWidget } from '../components/dashboard/WorkoutsWidget';
import { TasksWidget } from '../components/dashboard/TasksWidget';
import { AddFeelingEventModal } from '../components/calendar/add-event-modals/AddFeelingEventModal';
import { AddMedicationEventModal } from '../components/calendar/add-event-modals/AddMedicationEventModal';
import { AddWorkoutEventModal } from '../components/calendar/add-event-modals/AddWorkoutEventModal';
import { AddTaskEventModal } from '../components/calendar/add-event-modals/AddTaskEventModal';
import { AddFoodEventModal } from '../components/calendar/add-event-modals/AddFoodEventModal';
import { AddLiquidEventModal } from '../components/calendar/add-event-modals/AddLiquidEventModal';
import { WorkoutEventDetailModal } from '../components/dashboard/WorkoutEventDetailModal';
import { DateNavigator } from '../components/common/DateNavigator';
import { saveMedicationIntake } from '../services/medication.service';
import { saveWorkoutIntake } from '../services/workout.service';
import { createFeelingEvent, deleteCalendarEvent, updateCalendarEvent, completeEvent, updateFeelingEvent, createFluidEvent, updateFluidEvent, CreateFluidEventDto, UpdateFluidEventDto } from '../services/calendar.service';
import { saveFoodEvent } from '../services/food.service';
import { createTaskEvent, updateTaskEvent, CreateTaskEventDto, UpdateTaskEventDto } from '../services/task.service';
import { toastService } from '../services/toast.service';
import { localNowIso } from '@/utils/datetime';
import { ConfirmationDialog } from '@components/common/ConfirmationDialog.tsx';
import { CalendarEventDto } from '../types/calendar';
import { SortOrder } from '../services/workout.service';
import ExpertDashboardClientsCardList from '../components/dashboard/ExpertDashboardClientsCardList';
import ExpertAppointmentsWidget from '../components/dashboard/AppointmentsWidget';
import ConnectionRequestsWidget from '../components/dashboard/ConnectionRequestsWidget';
import MobileAppWidget from '../components/dashboard/MobileAppWidget';
import { DashboardAppointmentsWidget } from '../components/dashboard/DashboardAppointmentsWidget';
import { useVariant } from '../contexts/VariantContext';
import { DashboardPageV2 } from './dashboard-v2/DashboardPageV2';
import { PageHint } from '../components/common/PageHint';

const getErrorMessage = (error: any, defaultMessage: string): string => {
  if (error && error.response && error.response.data && error.response.data.message) {
    if (Array.isArray(error.response.data.message)) {
      return error.response.data.message.join(', ');
    }
    return defaultMessage;
  }
  return defaultMessage;
};

const toInputDateString = (date: Date) => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseInputDateString = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const DashboardPage: React.FC = () => {
  const { user, logout, fetchCurrentUser } = useAuth();
  const { variant } = useVariant();
  const navigate = useNavigate();
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isInitialSurveyModalOpen, setIsInitialSurveyModalOpen] = useState(false);
  const [isEmailVerificationModalOpen, setIsEmailVerificationModalOpen] = useState(false);
  const [dashboardData, setDashboardData] = useState<TodayDashboardResponse | null>(null);
  const [dashboardOptions, setDashboardOptions] = useState<DashboardOptionsResponse | null>(null);
  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [dashboardError, setDashboardError] = useState<string | null>(null);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const [selectedDate, setSelectedDate] = useState(toInputDateString(new Date()));
  const [sortOrder, setSortOrder] = useState<SortOrder>(() => {
    const savedSortOrder = localStorage.getItem('dashboardSortOrder');
    return (savedSortOrder === 'ASC' || savedSortOrder === 'DESC') ? savedSortOrder : 'ASC';
  });

  const [isAddFeelingModalOpen, setIsAddFeelingModalOpen] = useState(false);
  const [isAddMedicationModalOpen, setIsAddMedicationModalOpen] = useState(false);
  const [isAddWorkoutModalOpen, setIsAddWorkoutModalOpen] = useState(false);
  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false);
  const [isAddFoodModalOpen, setIsAddFoodModalOpen] = useState(false);
  const [isQuickAddFoodModalOpen, setIsQuickAddFoodModalOpen] = useState(false);
  const [isQuickAddWorkoutModalOpen, setIsQuickAddWorkoutModalOpen] = useState(false);
  const [isQuickAddMedicationModalOpen, setIsQuickAddMedicationModalOpen] = useState(false);
  const [isAddWaterModalOpen, setIsAddWaterModalOpen] = useState(false);

  const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] = useState(false);
  const [eventToDeleteId, setEventToDeleteId] = useState<number | null>(null);

  const [editingWorkoutEvent, setEditingWorkoutEvent] = useState<CalendarEventDto | null>(null);
  const [viewingWorkoutEvent, setViewingWorkoutEvent] = useState<CalendarEventDto | null>(null);
  const [editingFeelingEvent, setEditingFeelingEvent] = useState<CalendarEventDto | null>(null);
  const [editingFoodEvent, setEditingFoodEvent] = useState<CalendarEventDto | null>(null);
  const [viewingFoodEvent, setViewingFoodEvent] = useState<CalendarEventDto | null>(null);
  const [editingWaterEvent, setEditingWaterEvent] = useState<CalendarEventDto | null>(null);
  const [editingMedicationEvent, setEditingMedicationEvent] = useState<CalendarEventDto | null>(null);
  const [editingTaskEvent, setEditingTaskEvent] = useState<CalendarEventDto | null>(null);

  const isClient = user?.roles?.includes('client');
  const isExpert = user?.roles?.includes('expert');

  const fetchDashboardData = useCallback(async () => {
    setLoadingDashboard(true);
    setDashboardError(null);
    try {
      const data = await getTodayDashboard(selectedDate, sortOrder);
      setDashboardData(data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setDashboardError('Не вдалося завантажити дані дашборду.');
    } finally {
      setLoadingDashboard(false);
    }
  }, [selectedDate, sortOrder]);

  const fetchDashboardOptions = useCallback(async () => {
    setLoadingOptions(true);
    setOptionsError(null);
    try {
      const data = await getDashboardOptions();
      setDashboardOptions(data);
    } catch (error) {
      console.error('Error fetching dashboard options:', error);
      setOptionsError('Не вдалося завантажити опції дашборду.');
    } finally {
      setLoadingOptions(false);
    }
  }, []);

  useEffect(() => {
    if (!isExpert) {
      fetchDashboardData();
    }
  }, [fetchDashboardData, isExpert]);

  useEffect(() => {
    if (!isExpert) {
      fetchDashboardOptions();
    }
  }, [fetchDashboardOptions, isExpert]);

  useEffect(() => {
    localStorage.setItem('dashboardSortOrder', sortOrder);
  }, [sortOrder]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isAnyModalOpen = isChangePasswordOpen || isInitialSurveyModalOpen || isEmailVerificationModalOpen ||
                             isAddFeelingModalOpen || isAddMedicationModalOpen || isAddWorkoutModalOpen ||
                             isAddTaskModalOpen || isAddFoodModalOpen || isQuickAddFoodModalOpen ||
                             isQuickAddWorkoutModalOpen || isQuickAddMedicationModalOpen ||
                             isConfirmDeleteDialogOpen || isAddWaterModalOpen || 
                             Boolean(viewingWorkoutEvent) || Boolean(viewingFoodEvent);

      if (!isAnyModalOpen) {
        const currentDate = parseInputDateString(selectedDate);
        let newDate = new Date(currentDate);
        let dateChanged = false;

        if (event.key === 'ArrowLeft') {
          newDate.setDate(currentDate.getDate() - 1);
          dateChanged = true;
          event.preventDefault();
        } else if (event.key === 'ArrowRight') {
          newDate.setDate(currentDate.getDate() + 1);
          dateChanged = true;
          event.preventDefault();
        }

        if (dateChanged) {
          const newDateString = toInputDateString(newDate);
          setSelectedDate(newDateString);
          toastService.info(`Дата змінена на: ${newDateString}`);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedDate, setSelectedDate,
      isChangePasswordOpen, isInitialSurveyModalOpen, isEmailVerificationModalOpen,
      isAddFeelingModalOpen, isAddMedicationModalOpen, isAddWorkoutModalOpen,
      isAddTaskModalOpen, isAddFoodModalOpen, isQuickAddFoodModalOpen, 
      isQuickAddWorkoutModalOpen, isQuickAddMedicationModalOpen,
      isConfirmDeleteDialogOpen, isAddWaterModalOpen,
      viewingWorkoutEvent, viewingFoodEvent
  ]);

  const handleChangePasswordSuccess = () => {
    setIsChangePasswordOpen(false);
    setTimeout(() => logout(), 1000);
  };

  const handleChangePassword = () => {
    setIsChangePasswordOpen(true);
  };

  useEffect(() => {
    if (user) {
      if (!user.isEmailVerified) {
        setIsEmailVerificationModalOpen(true);
      } else if (isClient && !user.isFirstSurveyCompleted) {
        setIsInitialSurveyModalOpen(true);
      }
    }
  }, [user, isClient]);

  const handleInitialSurveyComplete = () => {
    setIsInitialSurveyModalOpen(false);
    fetchCurrentUser();
  };

  const handleInitialSurveySkip = () => {
    setIsInitialSurveyModalOpen(false);
  };

  const handleEmailVerificationSuccess = () => {
    setIsEmailVerificationModalOpen(false);
    fetchCurrentUser();
  };

  const handleEmailVerificationClose = () => {
    setIsEmailVerificationModalOpen(false);
  };

  const handleOpenAddFeelingModal = useCallback(() => {
    setEditingFeelingEvent(null);
    setIsAddFeelingModalOpen(true);
  }, []);

  const handleEditFeeling = useCallback((event: CalendarEventDto) => {
    setEditingFeelingEvent(event);
    setIsAddFeelingModalOpen(true);
  }, []);

  const handleCloseAddFeelingModal = useCallback(() => {
    setIsAddFeelingModalOpen(false);
    setEditingFeelingEvent(null);
  }, []);

  const handleSaveAndCloseAddFeelingModal = useCallback(async (data: { title: string; scheduledLdt: string; isCompleted: boolean }) => {
    setActionLoading(true);
    setActionError(null);
    try {
      if (editingFeelingEvent) {
        await updateFeelingEvent(editingFeelingEvent.id, {
          title: data.title,
          scheduledLdt: data.scheduledLdt,
          isCompleted: data.isCompleted,
        });
        toastService.success('Відчуття успішно оновлено');
      } else {
        await createFeelingEvent({
          title: data.title,
          scheduledLdt: data.scheduledLdt,
          isCompleted: data.isCompleted,
        });
        toastService.success('Відчуття успішно додано');
      }
      fetchDashboardData();
      handleCloseAddFeelingModal();
    } catch (error) {
      console.error('Failed to save feeling event:', error);
      const errorMessage = getErrorMessage(error, 'Не вдалося зберегти дані');
      setActionError(errorMessage);
      toastService.error(errorMessage);
    } finally {
      setActionLoading(false);
    }
  }, [fetchDashboardData, selectedDate, editingFeelingEvent, handleCloseAddFeelingModal]);

  const handleOpenAddMedicationModal = useCallback(() => {
    setEditingMedicationEvent(null);
    setIsAddMedicationModalOpen(true);
  }, []);

  const handleOpenQuickAddMedicationModal = useCallback(() => {
    setIsQuickAddMedicationModalOpen(true);
  }, []);

  const handleEditMedication = useCallback((event: CalendarEventDto) => {
    setEditingMedicationEvent(event);
    setIsAddMedicationModalOpen(true);
  }, []);

  const handleCloseAddMedicationModal = useCallback(() => {
    setIsAddMedicationModalOpen(false);
    setEditingMedicationEvent(null);
  }, []);
  
  const handleSaveAndCloseAddMedicationModal = useCallback(async (data: {
    medicationId?: number;
    customName?: string;
    qty?: number;
    dosage?: string;
    scheduledLdt: string; // local datetime string (no Z suffix)
    isCompleted: boolean;
    calendarId?: number;
  }) => {
    try {
      await saveMedicationIntake(data, { eventId: editingMedicationEvent?.id });
      toastService.success(editingMedicationEvent ? 'Прийом препарату успішно оновлено' : 'Прийом препарату успішно додано');
      fetchDashboardData();
      setIsAddMedicationModalOpen(false);
      setEditingMedicationEvent(null);
    } catch (error) {
      console.error('Failed to save medication intake:', error);
      toastService.error(getErrorMessage(error, 'Не вдалося зберегти дані'));
    }
  }, [fetchDashboardData, editingMedicationEvent]);

  const handleOpenAddWorkoutModal = useCallback(() => {
    setEditingWorkoutEvent(null);
    setIsAddWorkoutModalOpen(true);
  }, []);

  const handleOpenQuickAddWorkoutModal = useCallback(() => {
    setIsQuickAddWorkoutModalOpen(true);
  }, []);

  const handleEditWorkout = useCallback((event: CalendarEventDto) => {
    setEditingWorkoutEvent(event);
    setIsAddWorkoutModalOpen(true);
  }, []);

  const handleCloseAddWorkoutModal = useCallback(() => {
    setIsAddWorkoutModalOpen(false);
    setEditingWorkoutEvent(null);
  }, []);

  const handleSaveAndCloseAddWorkoutModal = useCallback(async (data: {
    workoutId?: number;
    customName?: string;
    name: string;
    durationMinutes?: number;
    calendarId?: number;
    scheduledAt: Date;
    isCompleted?: boolean;
    metValue?: number;
    metActivityId?: number;
    externalLink?: string;
    details?: string;
    defaultMeasurementValue?: number;
  }) => {
    try {
      await saveWorkoutIntake(data, { eventId: editingWorkoutEvent?.id });
      toastService.success(editingWorkoutEvent ? 'Тренування успішно оновлено' : 'Тренування успішно додано');
      fetchDashboardData();
      setIsAddWorkoutModalOpen(false);
      setEditingWorkoutEvent(null);
    } catch (error) {
      console.error('Failed to save workout:', error);
      toastService.error(getErrorMessage(error, 'Не вдалося зберегти дані'));
    }
  }, [fetchDashboardData, editingWorkoutEvent]);

  const handleOpenAddFoodModal = useCallback(() => {
    setEditingFoodEvent(null);
    setIsAddFoodModalOpen(true);
  }, []);

  const handleOpenQuickAddFoodModal = useCallback(() => {
    setIsQuickAddFoodModalOpen(true);
  }, []);

  const handleEditFood = useCallback((event: CalendarEventDto) => {
    setEditingFoodEvent(event);
    setIsAddFoodModalOpen(true);
  }, []);

  const handleCloseAddFoodModal = useCallback(() => {
    setIsAddFoodModalOpen(false);
    setEditingFoodEvent(null);
  }, []);

  const handleSaveAndCloseAddFoodModal = useCallback(async (data: {
    foodId?: number;
    customName?: string;
    name: string;
    measureKey?: string;
    quantity?: number;
    qty?: number;
    qtyG?: number;
    calendarId?: number;
    scheduledAt: Date;
    isCompleted: boolean;
    unit?: string;
    caloriesPer100g?: number;
    proteinPer100g?: number;
    fatPer100g?: number;
    carbsPer100g?: number;
    fiberPer100g?: number;
  }) => {
    try {
      await saveFoodEvent(data, { eventId: editingFoodEvent?.id });

      toastService.success(editingFoodEvent ? 'Прийом їжі успішно оновлено' : 'Прийом їжі успішно додано');
      fetchDashboardData();
      setIsAddFoodModalOpen(false);
      setEditingFoodEvent(null);
    } catch (error) {
      console.error('Failed to save food event:', error);
      toastService.error(getErrorMessage(error, 'Не вдалося зберегти дані'));
    }
  }, [fetchDashboardData, editingFoodEvent]);

  const handleOpenAddLiquidModal = useCallback(() => {
    setEditingWaterEvent(null);
    setIsAddWaterModalOpen(true);
  }, []);

  const handleEditWater = useCallback((event: CalendarEventDto) => {
    setEditingWaterEvent(event);
    setIsAddWaterModalOpen(true);
  }, []);

  const handleCloseAddWaterModal = useCallback(() => {
    setIsAddWaterModalOpen(false);
    setEditingWaterEvent(null);
  }, []);

  const handleSaveAndCloseAddWaterModal = useCallback(async (data: CreateFluidEventDto | (UpdateFluidEventDto & { id: number })) => {
    setActionLoading(true);
    setActionError(null);
    try {
      if ('id' in data) {
        await updateFluidEvent(data.id, { qty: data.qty, scheduledLdt: data.scheduledLdt, isCompleted: data.isCompleted });
        toastService.success('Запис про рідину успішно оновлено');
      } else {
        await createFluidEvent({ ...data, scheduledLdt: data.scheduledLdt || localNowIso() });
        toastService.success('Рідину успішно додано');
      }
      fetchDashboardData();
      handleCloseAddWaterModal();
    } catch (error) {
      console.error('Failed to save fluid event:', error);
      const errorMessage = getErrorMessage(error, 'Не вдалося зберегти дані');
      setActionError(errorMessage);
      toastService.error(errorMessage);
    } finally {
      setActionLoading(false);
    }
  }, [fetchDashboardData, handleCloseAddWaterModal]);

  const handleOpenAddTaskModal = useCallback(() => {
    setEditingTaskEvent(null);
    setIsAddTaskModalOpen(true);
  }, []);

  const handleEditTask = useCallback((event: CalendarEventDto) => {
    setEditingTaskEvent(event);
    setIsAddTaskModalOpen(true);
  }, []);

  const handleCloseAddTaskModal = useCallback(() => {
    setIsAddTaskModalOpen(false);
    setEditingTaskEvent(null);
  }, []);

  const handleSaveAndCloseAddTaskModal = useCallback(async (data: CreateTaskEventDto | UpdateTaskEventDto) => {
    setActionLoading(true);
    setActionError(null);
    try {
      if (editingTaskEvent) {
        await updateTaskEvent(editingTaskEvent.id, data as UpdateTaskEventDto);
        toastService.success('Задачу успішно оновлено');
      } else {
        await createTaskEvent(data as CreateTaskEventDto);
        toastService.success('Задачу успішно додано');
      }
      fetchDashboardData();
      handleCloseAddTaskModal();
    } catch (error) {
      console.error('Failed to save task event:', error);
      const errorMessage = getErrorMessage(error, 'Не вдалося зберегти дані');
      setActionError(errorMessage);
      toastService.error(errorMessage);
    } finally {
      setActionLoading(false);
    }
  }, [fetchDashboardData, editingTaskEvent, handleCloseAddTaskModal]);

  const handleDeleteRequest = useCallback((eventId: number) => {
    setEventToDeleteId(eventId);
    setIsConfirmDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (eventToDeleteId) {
      try {
        await deleteCalendarEvent(eventToDeleteId);
        toastService.success('Подію успішно видалено');
        fetchDashboardData();
      } catch (error) {
        toastService.error(getErrorMessage(error, 'Не вдалося видалити подію'));
      } finally {
        setIsConfirmDeleteDialogOpen(false);
        setEventToDeleteId(null);
      }
    }
  }, [eventToDeleteId, fetchDashboardData]);

  const handleCancelDelete = useCallback(() => {
    setIsConfirmDeleteDialogOpen(false);
    setEventToDeleteId(null);
  }, []);

  const handleToggleCalendarEventComplete = async (eventId: number, isCompleted: boolean) => {
    try {
      if (isCompleted) {
        await completeEvent(eventId);
      } else {
        await updateCalendarEvent(eventId, { isCompleted: false });
      }
      toastService.success(`Статус події оновлено`);
      fetchDashboardData();
    } catch (error) {
      console.error('Failed to update event status:', error);
      toastService.error(getErrorMessage(error, 'Не вдалося оновити статус події'));
    }
  };

  const handleOpenAddEventGlobally = useCallback((eventTypeCode: string) => {
    switch (eventTypeCode) {
      case 'feeling': handleOpenAddFeelingModal(); break;
      case 'medication': handleOpenAddMedicationModal(); break;
      case 'workout': handleOpenAddWorkoutModal(); break;
      case 'task': handleOpenAddTaskModal(); break;
      case 'food': handleOpenAddFoodModal(); break;
      case 'liquid': handleOpenAddLiquidModal(); break;
      default: navigate(`/calendar?openModal=${eventTypeCode}`); break;
    }
  }, [
    navigate,
    handleOpenAddFeelingModal,
    handleOpenAddMedicationModal,
    handleOpenAddWorkoutModal,
    handleOpenAddTaskModal,
    handleOpenAddFoodModal,
    handleOpenAddLiquidModal,
  ]);

  const isLoading = loadingDashboard || loadingOptions;

  if (isExpert) {
    return (
      <DashboardLayout data-i-doc={PageIndices.DASHBOARD}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <ExpertDashboardClientsCardList
              connectionStatus="active"
              title="Клієнти"
            />
          </Grid>
          <Grid item xs={12}>
            <ExpertAppointmentsWidget />
          </Grid>
          <Grid item xs={12}>
            <ConnectionRequestsWidget />
          </Grid>
          <Grid item xs={12} md={6}>
            <MobileAppWidget />
          </Grid>
        </Grid>
      </DashboardLayout>
    );
  }

  if (isLoading && !dashboardData) {
    return (
      <DashboardLayout data-i-doc={PageIndices.DASHBOARD} onChangePassword={handleChangePassword} onAddEventSelected={handleOpenAddEventGlobally} eventTypes={dashboardOptions?.eventTypes || []}>
        <Container maxWidth="lg" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Container>
      </DashboardLayout>
    );
  }

  const error = dashboardError || optionsError;
  if (error && !dashboardData) {
    return (
      <DashboardLayout data-i-doc={PageIndices.DASHBOARD} onChangePassword={handleChangePassword} onAddEventSelected={handleOpenAddEventGlobally} eventTypes={dashboardOptions?.eventTypes || []}>
        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Container>
      </DashboardLayout>
    );
  }

  const defaultCaloriesStats: CaloriesStats = { goal: 0, actual: 0, percentage: 0, remaining: 0 };
  const defaultMacroDetail: MacroDetail = { actual: 0, goal: 0, percentage: 0, remaining: 0 };
  const defaultWaterStats: WaterIntakeStats = { goal: 0, actual: 0, percentage: 0, remaining: 0 };
  const defaultWorkoutSummary: WorkoutSummary = { goal: 0, actual: 0, percentage: 0, unit: '' };

  const fluidData = dashboardData?.fluid || { summary: defaultWaterStats, events: [] };
  const feelingsData = dashboardData?.feelings || { summary: {}, events: [] };
  const medicationsData = dashboardData?.medications || { summary: {}, events: [] };
  const workoutsData = dashboardData?.workouts || { summary: defaultWorkoutSummary, events: [] };
  const tasksData = dashboardData?.tasks || { summary: {}, events: [] };
  const foodData = dashboardData?.food || { summary: { calories: defaultCaloriesStats, protein: defaultMacroDetail, fat: defaultMacroDetail, carbs: defaultMacroDetail }, events: [] };
  const appointmentsData = dashboardData?.appointments || { summary: {}, events: [] };

  if (variant === 'b') {
    return (
      <DashboardLayout data-i-doc={PageIndices.DASHBOARD} onChangePassword={handleChangePassword} onAddEventSelected={handleOpenAddEventGlobally} eventTypes={dashboardOptions?.eventTypes || []} disableContentPadding>
        <DashboardPageV2
          dashboardData={dashboardData}
          dashboardLoading={loadingDashboard}
          selectedDate={selectedDate}
          onDateChange={setSelectedDate}
          onRefresh={fetchDashboardData}
        />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout data-i-doc={PageIndices.DASHBOARD} onChangePassword={handleChangePassword} onAddEventSelected={handleOpenAddEventGlobally} eventTypes={dashboardOptions?.eventTypes || []}>
      <PageHint iDoc={PageIndices.DASHBOARD} />
      <Container maxWidth="lg">
        <Box sx={{ mb: 4, maxWidth: 300, mx: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <DateNavigator
            currentDate={selectedDate}
            onDateChange={setSelectedDate}
            label="Дата"
          />
          <FormControlLabel
            control={
              <Switch
                checked={sortOrder === 'DESC'}
                onChange={(e) => setSortOrder(e.target.checked ? 'DESC' : 'ASC')}
                name="sortOrder"
                color="primary"
              />
            }
            label={sortOrder === 'ASC' ? 'ASC' : 'DESC'}
            labelPlacement="end"
          />
        </Box>

        <Grid container spacing={3}>
          {isClient && dashboardOptions && (
            <>
              <Grid item xs={12} md={6}>
                <WaterIntakeWidget 
                  selectedDate={selectedDate} 
                  waterMeasures={dashboardOptions.waterMeasures}
                  waterStats={fluidData.summary}
                  waterEvents={fluidData.events}
                  onDataRefresh={fetchDashboardData}
                  isLoading={loadingDashboard}
                  error={dashboardError}
                  onAddLiquid={handleOpenAddLiquidModal}
                  onEditWater={handleEditWater}
                  onDeleteWater={handleDeleteRequest}
                  onToggleWaterComplete={handleToggleCalendarEventComplete}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <NutritionWidget
                  nutritionSummary={foodData.summary}
                  foodEvents={foodData.events}
                  onAddFood={handleOpenAddFoodModal}
                  onQuickAddFood={handleOpenQuickAddFoodModal}
                  onEditFood={handleEditFood}
                  onDeleteFood={handleDeleteRequest}
                  onToggleFoodComplete={handleToggleCalendarEventComplete}
                  onViewFood={(event) => setViewingFoodEvent(event)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <WorkoutsWidget 
                  workouts={workoutsData.events} 
                  workoutSummary={workoutsData.summary}
                  onAddWorkout={handleOpenAddWorkoutModal} 
                  onQuickAddWorkout={handleOpenQuickAddWorkoutModal}
                  onEditWorkout={handleEditWorkout}
                  onDeleteWorkout={handleDeleteRequest} 
                  onToggleWorkoutComplete={handleToggleCalendarEventComplete} 
                  onViewWorkout={(event) => setViewingWorkoutEvent(event)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FeelingsWidget 
                  feelings={feelingsData.events} 
                  onAddFeeling={handleOpenAddFeelingModal} 
                  onEditFeeling={handleEditFeeling}
                  onDeleteFeeling={handleDeleteRequest}
                  onToggleFeelingComplete={handleToggleCalendarEventComplete}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MedicationsWidget 
                  medications={medicationsData.events} 
                  onAddMedication={handleOpenAddMedicationModal}
                  onQuickAddMedication={handleOpenQuickAddMedicationModal}
                  onEditMedication={handleEditMedication}
                  onDeleteMedication={handleDeleteRequest}
                  onToggleMedicationComplete={handleToggleCalendarEventComplete}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TasksWidget
                  tasks={tasksData.events}
                  onAddTask={handleOpenAddTaskModal}
                  onEditTask={handleEditTask}
                  onDeleteTask={handleDeleteRequest}
                  onToggleTaskComplete={handleToggleCalendarEventComplete}
                />
              </Grid>
              {appointmentsData.events.length > 0 && (
                <Grid item xs={12} md={6}>
                  <DashboardAppointmentsWidget appointments={appointmentsData.events} />
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Container>

      <Dialog open={isChangePasswordOpen} onClose={() => setIsChangePasswordOpen(false)} maxWidth="sm" fullWidth>
        <ChangePasswordForm onSuccess={handleChangePasswordSuccess} onCancel={() => setIsChangePasswordOpen(false)} />
      </Dialog>

      <InitialSurveyModal open={isInitialSurveyModalOpen} onClose={handleInitialSurveySkip} onSurveyCompleted={handleInitialSurveyComplete} />

      {user?.email && (
        <EmailVerificationModal open={isEmailVerificationModalOpen} onClose={handleEmailVerificationClose} userEmail={user.email} onVerificationSuccess={handleEmailVerificationSuccess} />
      )}

      <AddFeelingEventModal
        open={isAddFeelingModalOpen}
        onClose={handleCloseAddFeelingModal}
        onSave={handleSaveAndCloseAddFeelingModal}
        eventToEdit={editingFeelingEvent}
        loading={actionLoading}
        error={actionError}
        initialScheduledAt={parseInputDateString(selectedDate)}
      />
      <AddMedicationEventModal
        open={isAddMedicationModalOpen}
        onClose={handleCloseAddMedicationModal}
        onSave={handleSaveAndCloseAddMedicationModal}
        calendarId={undefined}
        eventToEdit={editingMedicationEvent}
      />
      <AddWorkoutEventModal open={isAddWorkoutModalOpen} onClose={handleCloseAddWorkoutModal} onSave={handleSaveAndCloseAddWorkoutModal} calendarId={undefined} eventToEdit={editingWorkoutEvent} />
      <AddTaskEventModal 
        open={isAddTaskModalOpen} 
        onClose={handleCloseAddTaskModal} 
        onSave={handleSaveAndCloseAddTaskModal} 
        eventToEdit={editingTaskEvent}
        loading={actionLoading}
        error={actionError}
        showPriority={false}
      />
      <AddFoodEventModal
        open={isAddFoodModalOpen}
        onClose={handleCloseAddFoodModal}
        onSave={handleSaveAndCloseAddFoodModal}
        calendarId={undefined}
        eventToEdit={editingFoodEvent}
      />
      <FoodEventDetailModal
        open={Boolean(viewingFoodEvent)}
        onClose={() => setViewingFoodEvent(null)}
        event={viewingFoodEvent}
        onEdit={(event) => {
          setViewingFoodEvent(null);
          handleEditFood(event);
        }}
      />
      
      <QuickFoodAddModal
        open={isQuickAddFoodModalOpen}
        onClose={() => setIsQuickAddFoodModalOpen(false)}
        selectedDate={selectedDate}
        onRefreshDashboard={fetchDashboardData}
      />

      <QuickWorkoutAddModal
        open={isQuickAddWorkoutModalOpen}
        onClose={() => setIsQuickAddWorkoutModalOpen(false)}
        selectedDate={selectedDate}
        onRefreshDashboard={fetchDashboardData}
      />

      <QuickMedicationAddModal
        open={isQuickAddMedicationModalOpen}
        onClose={() => setIsQuickAddMedicationModalOpen(false)}
        selectedDate={selectedDate}
        onRefreshDashboard={fetchDashboardData}
      />

      <WorkoutEventDetailModal
        open={Boolean(viewingWorkoutEvent)}
        onClose={() => setViewingWorkoutEvent(null)}
        event={viewingWorkoutEvent}
        onEdit={(event) => {
          setViewingWorkoutEvent(null);
          handleEditWorkout(event);
        }}
      />

      <AddLiquidEventModal
        open={isAddWaterModalOpen}
        onClose={handleCloseAddWaterModal}
        onSave={handleSaveAndCloseAddWaterModal}
        calendarId={undefined}
        eventToEdit={editingWaterEvent}
      />

      <ConfirmationDialog
        open={isConfirmDeleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Підтвердити видалення"
        message="Ви впевнені, що хочете видалити цю подію? Цю дію неможливо буде скасувати."
        confirmText="Видалити"
        cancelText="Скасувати"
      />
    </DashboardLayout>
  );
};
